import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Collapse,
  Button,
  Divider,
  Switch,
  FormControlLabel as SwitchLabel,
} from "@mui/material";
import {
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import { compactToolbarConfig, descriptionToolbarConfig, updateFontSizeLabel } from "./QuillConfig";
import SingleChoiceQuestion from "./QuestionTypes/SingleChoiceQuestion";
import MultiChoiceQuestion from "./QuestionTypes/MultiChoiceQuestion";
import TextQuestion from "./QuestionTypes/TextQuestion";
import LikertScaleQuestion from "./QuestionTypes/LikertScaleQuestion";
import OpenTextQuestion from "./QuestionTypes/OpenTextQuestion";
import PageBreak from "./QuestionTypes/PageBreak";


// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

function QuestionCard({
  question,
  index,
  handleQuestionChange,
  handleQuestionSettingsChange,
  handleQuestionStylesChange,
  handleAddChoice,
  handleChoiceChange,
  handleDeleteQuestion,
  handleDuplicateQuestion,
  settings,
  isDeleting,
  enqueueSnackbar, // Add this prop
  // The linked project's segments a single-choice question can be linked to.
  segments = [],
  usedSegmentIds = [],
}) {
  const questionStyles = question.settings?.styles?.question || {
    fontWeight: "normal",
    fontStyle: "normal",
    fontSize: "14px",
    lineHeight: "1.15",
    letterSpacing: "0px",
  };
  const descriptionStyles = question.settings?.styles?.description || {
    fontWeight: "normal",
    fontStyle: "normal",
    fontSize: "14px",
    lineHeight: "1.15",
    letterSpacing: "0px",
  };

  // State for controlling visibility of optional sections
  const [showDescription, setShowDescription] = useState(false);
  const quillRef = useRef(null);
  const descriptionQuillRef = useRef(null);

  const debouncedHandleQuestionChange = useRef(
    debounce((index, field, value) => {
      if (isDeleting) {
        console.log(`${field} Quill change ignored: Deletion in progress`);
        return;
      }
      handleQuestionChange(index, field, value);
    }, 50) // Reduced from 300ms to 50ms for better typing responsiveness
  ).current;

  const handleDeleteClick = () => {
    handleDeleteQuestion(index);
  };

  useEffect(() => {
    return () => {
      if (window.ResizeObserver) {
        const observers = document.querySelectorAll('.ql-container');
        observers.forEach((el) => {
          const observer = new ResizeObserver(() => { });
          observer.disconnect();
        });
      }
    };
  }, []);

  useEffect(() => {
    // Use a small delay to ensure the editor is fully initialized
    const timer = setTimeout(() => {
      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        if (quill) {
          updateFontSizeLabel(quill);
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [question.question]);

  // Initialize font size label updater for description editor when ready
  useEffect(() => {
    if (showDescription) {
      // Use a small delay to ensure the editor is fully initialized
      const timer = setTimeout(() => {
        if (descriptionQuillRef.current) {
          const quill = descriptionQuillRef.current.getEditor();
          if (quill) {
            updateFontSizeLabel(quill);
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showDescription, question.description]);

  const renderQuestionType = () => {
    switch (question.type) {
      case "SINGLE_CHOICE":
        return (
          <SingleChoiceQuestion
            question={question}
            index={index}
            handleChoiceChange={handleChoiceChange}
            handleQuestionChange={handleQuestionChange}
            handleAddChoice={handleAddChoice}
            isDeleting={isDeleting}
            enqueueSnackbar={enqueueSnackbar}
            segments={segments}
            usedSegmentIds={usedSegmentIds}
            // Once the survey has responses the BE refuses to move an existing
            // question's link (a new question can still link).
            segmentLocked={Boolean(settings?.projectLocked) && typeof question.id === "number"}
          />
        );
      case "MULTI_CHOICE":
        return (
          <MultiChoiceQuestion
            question={question}
            index={index}
            handleChoiceChange={handleChoiceChange}
            handleAddChoice={handleAddChoice}
            isDeleting={isDeleting}
            enqueueSnackbar={enqueueSnackbar}
          />
        );
      case "TEXT":
        return <TextQuestion />;
      case "LIKERT_SCALE":
        return (
          <LikertScaleQuestion
            question={question}
            index={index}
            handleQuestionChange={handleQuestionChange}
            isDeleting={isDeleting}
          />
        );
      case "OPEN_TEXT":
        return (
          <OpenTextQuestion
            question={question}
            index={index}
            handleQuestionChange={handleQuestionChange}
            isDeleting={isDeleting}
          />
        );
      case "PAGE_BREAK":
        return (
          <PageBreak
            index={index}
            handleDeleteQuestion={handleDeleteQuestion}
            handleDuplicateQuestion={handleDuplicateQuestion}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      {question.type === "PAGE_BREAK" ? (
        renderQuestionType()
      ) : (
        <Card
          sx={{
            border: "1px solid #1976d2",
            borderRadius: "8px",
            p: 3,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            backgroundColor: "#ffffff",
            color: "#000000",
          }}
        >
          <CardContent sx={{ p: 0 }}>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontSize: "0.875rem", color: "#000000" }}
              >
                {question.type === "TEXT" ? "Content" : "Question"}
              </Typography>
              {!isDeleting ? (
                <ReactQuill
                  ref={quillRef}
                  value={question.question || ""}
                  onChange={(value) => debouncedHandleQuestionChange(index, "question", value)}
                  placeholder={question.type === "TEXT" ? "Enter your heading or content..." : "Enter your question..."}
                  modules={compactToolbarConfig}
                  formats={["bold", "italic", "underline", "color", "size", "lineheight", "letterspacing", "align"]}
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "4px",
                    color: "#000000",
                    fontWeight: questionStyles.fontWeight,
                    fontStyle: questionStyles.fontStyle,
                    fontSize: questionStyles.fontSize,
                    lineHeight: questionStyles.lineHeight,
                    letterSpacing: questionStyles.letterSpacing,
                  }}
                />
              ) : (
                <Typography variant="body2" sx={{ color: "#000000" }}>
                  Deleting...
                </Typography>
              )}
            </Box>

            {/* Question Type Specific Content */}
            {renderQuestionType()}

            <Divider sx={{ my: 2, borderColor: "rgba(0, 0, 0, 0.12)" }} />

            {/* Advanced Options Toggle Buttons */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.875rem", color: "#000000", fontWeight: 500 }}
                >
                  Advanced Options
                </Typography>
                {question.type !== "TEXT" && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.875rem", color: "#000000" }}
                    >
                      Required:
                    </Typography>
                    <SwitchLabel
                      control={
                        <Switch
                          checked={question.isMandatory || false}
                          onChange={(e) => {
                            if (isDeleting) {
                              console.log("Mandatory change ignored: Deletion in progress");
                              return;
                            }
                            handleQuestionChange(
                              index,
                              "isMandatory",
                              e.target.checked
                            );
                          }}
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#1976d2',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#1976d2',
                            },
                          }}
                        />
                      }
                      label=""
                      sx={{ margin: 0 }}
                    />
                  </Box>
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DescriptionIcon />}
                  endIcon={showDescription ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => {
                    if (showDescription) {
                      setShowDescription(false);
                    } else {
                      setShowDescription(true);
                    }
                  }}
                  sx={{
                    borderColor: "#000000",
                    color: "#000000",
                    "&:hover": {
                      borderColor: "#000000",
                      backgroundColor: "rgba(25, 118, 210, 0.04)",
                    },
                    fontSize: "0.75rem",
                    minHeight: "32px",
                  }}
                >
                  Description
                </Button>
              </Box>
            </Box>

            {/* Description Section */}
            <Collapse in={showDescription}>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, fontSize: "0.875rem", color: "#000000" }}
                >
                  Description (Optional)
                </Typography>
                {!isDeleting ? (
                  <ReactQuill
                    ref={descriptionQuillRef}
                    value={question.description || ""}
                    onChange={(value) => debouncedHandleQuestionChange(index, "description", value)}
                    placeholder="Enter a description..."
                    modules={descriptionToolbarConfig}
                    formats={[
                      "bold",
                      "italic",
                      "underline",
                      "color",
                      "size",
                      "lineheight",
                      "letterspacing",
                      "align",
                      "link",
                      "list",
                      "bullet",
                    ]}
                    style={{
                      backgroundColor: "#ffffff",
                      borderRadius: "4px",
                      color: "#000000",
                      fontWeight: descriptionStyles.fontWeight,
                      fontStyle: descriptionStyles.fontStyle,
                      fontSize: descriptionStyles.fontSize,
                      lineHeight: descriptionStyles.lineHeight,
                      letterSpacing: descriptionStyles.letterSpacing,
                    }}
                  />
                ) : (
                  <Typography variant="body2" sx={{ color: "#000000" }}>
                    Deleting...
                  </Typography>
                )}
              </Box>
            </Collapse>

          </CardContent>

          <Box sx={{ position: "absolute", top: 8, right: 8 }}>
            <IconButton onClick={() => handleDuplicateQuestion(index)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={handleDeleteClick}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Card>
      )}
    </Box>
  );
}

export default QuestionCard;
